/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { MutableRefObject } from 'react';

// TODO - Remove this once we move this to wag-react
import AsyncCreatable, { Props as AsyncCreatableProps } from 'react-select/async-creatable';
import Async, { Props as AsyncProps } from 'react-select/async';
import { StylesConfigFunction } from 'react-select/src/styles';
import { HelperText, HelperTextProps } from './HelperText';
import { tryCatchSync } from '../../utils/tryCatch';
import { Box, BoxProps } from '../../wag-react/next-components/box';
type Variant = 'outlined' | 'filled';
export type AutocompleteProps = AsyncProps<unknown, boolean> & AsyncCreatableProps<unknown, boolean> & {
  // This is just a workaround to create on blur. Currently, it is not supported
  // https://github.com/JedWatson/react-select/issues/1764
  allowCreateNewOptionOnBlur?: boolean;
  allowEditCurrentOptionOnFocus?: boolean;
  error?: boolean;
  helperText?: string;
  helperTextProps?: HelperTextProps;
  isCreatable?: boolean;
  label?: string;
  name?: string;
  rootProps?: BoxProps;
  variant?: Variant;
};

// Export the styles to allow consumer to do styles composition
export const styles: {
  [key: string]: StylesConfigFunction<AutocompleteProps>;
} = {
  control: (provided, state) => ({
    ...provided,
    background: 'white',
    padding: '8px',
    borderRadius: '8px',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: state.selectProps.error && 'red' || (state.selectProps.variant === 'filled' ? '#EDEDED' : '#D1D1D1'),
    backgroundColor: state.selectProps.variant === 'filled' ? '#EDEDED' : 'white'
  }),
  placeholder: provided => ({
    ...provided,
    color: '#AAAAAA',
    fontFamily: 'muli'
  }),
  singleValue: provided => ({
    ...provided,
    color: '#727272',
    fontFamily: 'muli'
  }),
  dropdownIndicator: provided => ({
    ...provided,
    padding: '2px'
  }),
  valueContainer: provided => ({
    ...provided,
    padding: 0
  }),
  input: provided => ({
    ...provided,
    padding: 0,
    margin: 0
  }),
  indicatorsContainer: provided => ({
    ...provided,
    height: '24px'
  })
};

// https://github.com/JedWatson/react-select/issues/1764
const runImperativeCreateNewOptionOnBlur = (optionToCreate: unknown) => (selectRefInstance: MutableRefObject<any>) => {
  const createOption = (option: unknown) => ({
    label: option,
    value: option
  });
  if (!selectRefInstance || !selectRefInstance.current) {
    return;
  }
  const [setValue] = tryCatchSync(() => selectRefInstance.current.select.select.setValue || selectRefInstance.current.select.select.select.setValue);
  if (!setValue || typeof setValue !== 'function') {
    return;
  }
  if (!optionToCreate) {
    return;
  }
  setValue(createOption(optionToCreate), 'select-option');
};

// https://github.com/JedWatson/react-select/issues/1558#issuecomment-471657335
const runImperativeEditCurrentOptionOnFocus = (currentValue: any) => (selectRefInstance: MutableRefObject<any>) => {
  if (!selectRefInstance || !selectRefInstance.current) {
    return;
  }
  if (!selectRefInstance.current.select) {
    return;
  }
  if (!currentValue || !currentValue.label) {
    return;
  }
  // eslint-disable-next-line no-param-reassign
  selectRefInstance.current.select.state.inputValue = currentValue.label;
};
const runImperativeBlurOnMenuClose = (selectRefInstance: MutableRefObject<any>) => {
  tryCatchSync(() => {
    // We need to call the `.blur` on the select instance
    // We can't re-assign it as it will lose the reference to it
    if (selectRefInstance.current.select.select.blur) {
      selectRefInstance.current.select.select.blur();
      return;
    }
    if (selectRefInstance.current.select.select.select.blur) {
      selectRefInstance.current.select.select.select.blur();
    }
  });
};

// TODO - Move to wag-react (please!)
// TODO - Support changing of variants
export const Autocomplete = ({
  name,
  allowCreateNewOptionOnBlur,
  allowEditCurrentOptionOnFocus,
  label,
  helperText,
  helperTextProps,
  isCreatable,
  rootProps,
  // most of the time we should use `styles`
  loadOptions,
  options,
  onBlur,
  value,
  variant = 'outlined',
  ...rest
}: AutocompleteProps) => {
  const Component = isCreatable ? AsyncCreatable : Async;
  const ref = React.useRef(null);
  return <Box {...rootProps} data-sentry-element="Box" data-sentry-component="Autocomplete" data-sentry-source-file="index.tsx">
      <Component isClearable id={name} styles={styles} placeholder={label} createOptionPosition="first" allowCreateWhileLoading={false} defaultOptions={options} cacheOptions loadOptions={loadOptions}
    // This will remove the default "Create: <word>" and return the <word> only
    formatCreateLabel={createLabelValue => createLabelValue} ref={ref} value={value} onMenuOpen={() => {
      if (ref && allowEditCurrentOptionOnFocus) {
        runImperativeEditCurrentOptionOnFocus(value)(ref);
      }
    }} onMenuClose={() => runImperativeBlurOnMenuClose(ref)} onBlur={ev => {
      if (allowCreateNewOptionOnBlur && isCreatable) {
        ev.persist();
        const eventValueAsString = (ev.target as any).value;
        runImperativeCreateNewOptionOnBlur(eventValueAsString)(ref);
      }
      if (!onBlur) {
        return;
      }
      onBlur(ev);
    }} variant={variant} {...rest} data-sentry-element="Component" data-sentry-source-file="index.tsx" />
      {helperText && <HelperText text={helperText} error={rest.error} {...helperTextProps} />}
    </Box>;
};